import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CurrencyEuroIcon,
  CalendarDaysIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import apiService from "../services/api";

const UserProfileForm = ({ user = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    password: "",
    confirmPassword: "",
    role: user?.role || "mechanic",
    hourlyRate: user?.hourlyRate || 0,
    dateOfJoining: user?.dateOfJoining
      ? new Date(user.dateOfJoining).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    employmentStatus: user?.employmentStatus || "active",
  });

  const [documents, setDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState(
    user?.documents || []
  );
  const [documentTypes, setDocumentTypes] = useState([]);
  const [adminCreationReason, setAdminCreationReason] = useState("");
  const [showAdminConfirmation, setShowAdminConfirmation] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    newPassword: "",
    confirmNewPassword: ""
  });

  const documentTypeOptions = [
    { value: "contract", label: t("userProfile.documents.types.contract") },
    { value: "id", label: t("userProfile.documents.types.id") },
    { value: "tax_form", label: t("userProfile.documents.types.taxForm") },
    {
      value: "certificate",
      label: t("userProfile.documents.types.certificate"),
    },
    { value: "other", label: t("userProfile.documents.types.other") },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for role change
    if (name === "role" && value === "admin" && !user) {
      setShowAdminConfirmation(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRoleConfirmation = (confirmed) => {
    if (confirmed) {
      setFormData((prev) => ({
        ...prev,
        role: "admin",
      }));
    }
    setShowAdminConfirmation(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newDocuments = files.map((file) => ({ file, type: "other" }));
      setDocuments((prev) => [...prev, ...newDocuments]);
      setDocumentTypes((prev) => [
        ...prev,
        ...new Array(files.length).fill("other"),
      ]);
    }
  };

  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
    setDocumentTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDocumentType = (index, type) => {
    setDocumentTypes((prev) => {
      const newTypes = [...prev];
      newTypes[index] = type;
      return newTypes;
    });
  };

  const deleteExistingDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?"))
      return;

    try {
      await apiService.deleteUserDocument(user._id, documentId);
      setExistingDocuments((prev) =>
        prev.filter((doc) => doc._id !== documentId)
      );
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Failed to delete document");
    }
  };

  const previewDocument = async (documentId) => {
    try {
      const response = await apiService.getDocumentUrl(user._id, documentId);
      window.open(response.url, "_blank");
    } catch (error) {
      console.error("Error getting document URL:", error);
      alert("Failed to open document");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim())
      newErrors.name = t("userProfile.validation.nameRequired");
    if (!formData.email.trim())
      newErrors.email = t("userProfile.validation.emailRequired");
    if (!formData.phone.trim())
      newErrors.phone = t("userProfile.validation.phoneRequired");

    // Password validation (only for new users)
    if (!user) {
      if (!formData.password)
        newErrors.password = t("userProfile.validation.passwordRequired");
      else if (formData.password.length < 6)
        newErrors.password = t("userProfile.validation.passwordTooShort");

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t(
          "userProfile.validation.passwordMismatch"
        );
      }
    }

    // Email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = t("userProfile.validation.emailInvalid");
    }

    // Hourly rate
    if (formData.hourlyRate < 0) {
      newErrors.hourlyRate = t("userProfile.validation.hourlyRateNegative");
    }

    // Admin creation reason (for new admin users)
    if (formData.role === "admin" && !user) {
      if (!adminCreationReason.trim()) {
        newErrors.adminCreationReason = t(
          "userProfile.validation.adminReasonRequired"
        );
      } else if (adminCreationReason.trim().length < 10) {
        newErrors.adminCreationReason = t(
          "userProfile.validation.adminReasonTooShort"
        );
      } else if (adminCreationReason.trim().length > 200) {
        newErrors.adminCreationReason = t(
          "userProfile.validation.adminReasonTooLong"
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Validate password change if editing and password fields are filled
    if (user && showPasswordChange) {
      if (!passwordChangeData.newPassword || !passwordChangeData.confirmNewPassword) {
        alert("Please fill in both password fields or cancel password change");
        return;
      }
      if (passwordChangeData.newPassword.length < 6) {
        alert("New password must be at least 6 characters");
        return;
      }
      if (passwordChangeData.newPassword !== passwordChangeData.confirmNewPassword) {
        alert("New passwords do not match");
        return;
      }
    }

    setLoading(true);
    try {
      const submitData = new FormData();

      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== "confirmPassword" && key !== "password") {
          submitData.append(key, value);
        }
      });

      // Add documents
      documents.forEach((doc, index) => {
        submitData.append("documents", doc.file);
      });

      // Add document types
      if (documentTypes.length > 0) {
        submitData.append("documentTypes", documentTypes.join(","));
      }

      // Add admin creation reason if creating admin
      if (formData.role === "admin" && !user && adminCreationReason.trim()) {
        submitData.append("adminCreationReason", adminCreationReason.trim());
      }

      // Add password for new users
      if (!user && formData.password) {
        submitData.append("password", formData.password);
      }

      let response;
      if (user) {
        // First update user profile
        response = await apiService.updateUserProfile(user._id, submitData);

        // Then reset password if requested
        if (showPasswordChange && passwordChangeData.newPassword) {
          await apiService.resetUserPassword(user._id, passwordChangeData.newPassword);
          alert("User profile and password updated successfully!");
        }
      } else {
        response = await apiService.createUserWithProfile(submitData);
      }

      if (response.success) {
        onSuccess(response.user);
      }
    } catch (error) {
      console.error("Error saving user:", error);
      alert(error.message || "Failed to save user profile");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {user ? t("userProfile.title.edit") : t("userProfile.title.create")}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("userProfile.sections.basicInfo")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.fullName")} *
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("userProfile.placeholders.fullName")}
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.email")} *
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("userProfile.placeholders.email")}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.phone")} *
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("userProfile.placeholders.phone")}
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.role")} *
              </label>
              <div className="space-y-3">
                {/* Mechanic Option */}
                <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="role"
                    value="mechanic"
                    checked={formData.role === "mechanic"}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <div className="ml-3 flex items-center">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {t("userProfile.roles.mechanic.title")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t("userProfile.roles.mechanic.description")}
                      </div>
                    </div>
                  </div>
                </label>

                {/* Admin Option */}
                <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={formData.role === "admin"}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-red-600"
                  />
                  <div className="ml-3 flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-red-600 mr-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center">
                        {t("userProfile.roles.admin.title")}
                        <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 ml-1" />
                      </div>
                      <div className="text-xs text-gray-500">
                        {t("userProfile.roles.admin.description")}
                      </div>
                    </div>
                  </div>
                </label>

                {/* Admin Role Warning */}
                {formData.role === "admin" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mt-0.5 mr-2" />
                      <div className="text-sm">
                        <div className="font-medium text-amber-800">
                          {t("userProfile.adminWarning.title")}
                        </div>
                        <div className="text-amber-700 mt-1">
                          {t("userProfile.adminWarning.description")}
                          <ul className="list-disc list-inside mt-1 text-xs">
                            <li>
                              {t(
                                "userProfile.adminWarning.privileges.userManagement"
                              )}
                            </li>
                            <li>
                              {t(
                                "userProfile.adminWarning.privileges.systemConfig"
                              )}
                            </li>
                            <li>
                              {t(
                                "userProfile.adminWarning.privileges.dataExport"
                              )}
                            </li>
                            <li>
                              {t(
                                "userProfile.adminWarning.privileges.financial"
                              )}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Employment Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("userProfile.sections.employmentDetails")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date of Joining */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.dateOfJoining")}
              </label>
              <div className="relative">
                <CalendarDaysIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
            </div>

            {/* Hourly Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.hourlyRate")}
              </label>
              <div className="relative">
                <CurrencyEuroIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.hourlyRate ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.hourlyRate && (
                <p className="text-red-500 text-sm mt-1">{errors.hourlyRate}</p>
              )}
            </div>

            {/* Employment Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("userProfile.fields.employmentStatus")}
              </label>
              <div className="flex items-center space-x-4 mt-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="employmentStatus"
                    value="active"
                    checked={formData.employmentStatus === "active"}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("userProfile.status.active")}
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="employmentStatus"
                    value="inactive"
                    checked={formData.employmentStatus === "inactive"}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("userProfile.status.inactive")}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Password Section (only for new users) */}
        {!user && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("userProfile.sections.security")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("userProfile.fields.password")} *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("userProfile.placeholders.password")}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("userProfile.fields.confirmPassword")} *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder={t("userProfile.placeholders.confirmPassword")}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Password Change Section (only for existing users) */}
        {user && (
          <div className="bg-gray-50 p-4 rounded-lg border-2 border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <KeyIcon className="h-5 w-5 mr-2 text-purple-600" />
                Change Password
              </h3>
              {!showPasswordChange && (
                <button
                  type="button"
                  onClick={() => setShowPasswordChange(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Change Password
                </button>
              )}
            </div>

            {showPasswordChange && (
              <div className="space-y-4 mt-4 bg-white p-4 rounded-lg border border-purple-200">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-purple-800">
                    Note: This will reset the user's password. The user will need to use the new password to log in.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password *
                    </label>
                    <input
                      type="password"
                      value={passwordChangeData.newPassword}
                      onChange={(e) => setPasswordChangeData({
                        ...passwordChangeData,
                        newPassword: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                      placeholder="Enter new password"
                      minLength={6}
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password *
                    </label>
                    <input
                      type="password"
                      value={passwordChangeData.confirmNewPassword}
                      onChange={(e) => setPasswordChangeData({
                        ...passwordChangeData,
                        confirmNewPassword: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordChangeData({ newPassword: "", confirmNewPassword: "" });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admin Creation Reason (only for new admin users) */}
        {!user && formData.role === "admin" && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              {t("userProfile.adminCreation.title")}
            </h3>
            <div className="mb-4">
              <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                  <div className="text-sm text-red-800">
                    <div className="font-medium">
                      {t("userProfile.adminCreation.securityNotice.title")}
                    </div>
                    <div className="mt-1">
                      {t(
                        "userProfile.adminCreation.securityNotice.description"
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-medium text-red-800 mb-2">
                {t("userProfile.adminCreation.reasonLabel")} *
              </label>
              <textarea
                value={adminCreationReason}
                onChange={(e) => setAdminCreationReason(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none ${
                  errors.adminCreationReason
                    ? "border-red-500"
                    : "border-red-300"
                }`}
                rows={4}
                placeholder={t("userProfile.adminCreation.reasonPlaceholder")}
                maxLength={200}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.adminCreationReason && (
                  <p className="text-red-600 text-sm">
                    {errors.adminCreationReason}
                  </p>
                )}
                <span className="text-xs text-red-600 ml-auto">
                  {adminCreationReason.length}/200{" "}
                  {t("userProfile.adminCreation.characters")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Existing Documents (for edit mode) */}
        {user && existingDocuments.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("userProfile.sections.existingDocuments")}
            </h3>
            <div className="space-y-2">
              {existingDocuments.map((doc) => (
                <div
                  key={doc._id}
                  className="flex items-center justify-between p-3 bg-white rounded border"
                >
                  <div className="flex items-center space-x-3">
                    <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {documentTypeOptions.find(
                          (opt) => opt.value === doc.type
                        )?.label || doc.type}{" "}
                        •{doc.size ? formatFileSize(doc.size) : "Unknown size"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => previewDocument(doc._id)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                      title="Preview document"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteExistingDocument(doc._id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Delete document"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Document Upload */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("userProfile.sections.documents")}
          </h3>

          {/* File Upload Button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t("userProfile.documents.addDocuments")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("userProfile.documents.supportedFormats")}
            </p>
          </div>

          {/* Selected Documents */}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white rounded border"
                >
                  <div className="flex items-center space-x-3">
                    <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {doc.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.file.size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={documentTypes[index] || "other"}
                      onChange={(e) =>
                        updateDocumentType(index, e.target.value)
                      }
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                      {documentTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {t("userProfile.actions.cancel")}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? t("userProfile.actions.saving")
              : user
              ? t("userProfile.actions.update")
              : t("userProfile.actions.create")}
          </button>
        </div>
      </form>

      {/* Admin Role Confirmation Modal */}
      {showAdminConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                {t("userProfile.adminConfirmation.title")}
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                {t("userProfile.adminConfirmation.message")}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>
                    {t("userProfile.adminConfirmation.warning.title")}
                  </strong>{" "}
                  {t("userProfile.adminConfirmation.warning.description")}
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 mt-2">
                  <li>
                    {t(
                      "userProfile.adminConfirmation.warning.privileges.accounts"
                    )}
                  </li>
                  <li>
                    {t(
                      "userProfile.adminConfirmation.warning.privileges.financial"
                    )}
                  </li>
                  <li>
                    {t(
                      "userProfile.adminConfirmation.warning.privileges.system"
                    )}
                  </li>
                  <li>
                    {t(
                      "userProfile.adminConfirmation.warning.privileges.adminManagement"
                    )}
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleRoleConfirmation(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t("userProfile.adminConfirmation.cancel")}
              </button>
              <button
                onClick={() => handleRoleConfirmation(true)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {t("userProfile.adminConfirmation.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileForm;
